import type { ModalProps } from 'antd'
import { Alert, Button, Descriptions, Flex, Modal, Skeleton, Typography } from 'antd'
import { RiseOutlined } from '@ant-design/icons'
import React, { useState } from 'react'
import dayjs from 'dayjs'
import { useAuth } from '../../auth'
import { useTranslation } from 'react-i18next'
import { useCompanyUsage } from '../../company/hooks'
import { CompanyUsage } from '../../company/company'
import { SHOW_COMPANY_USAGE } from '../../user/permission'
import { usePricing, useUpgradePreview, useUpgradeTier } from '../hooks'
import { UpgradePreview } from '../api'
import { AGENCY, GROWTH, PRO, STARTER, Tier, WEBSITE } from '../tier'
import { formatPrice } from '../pricing'
import { PlanFeature, usePlanFeatures } from './plan-features'
import { AgencyTier, TIER_TAGLINES, TierCard, TierCardProps, useTierPrice } from './tier-card'
import clsx from 'clsx'

type UpgradeModalProps = {
    open: boolean
    onClose: () => void
} & Omit<ModalProps, 'open' | 'onCancel'>

// Self-service upgrade targets, ordered low → high. Agency is excluded: it is a
// contact-sales tier provisioned by an admin, never reached via self-service.
const ORDERED_TIERS: Tier[] = [STARTER, GROWTH, PRO]

// Full ranking incl. Agency as the highest, used to decide which tiers are above
// the current one. Agency outranks every self-service tier so an agency company
// is never shown upgrade options.
const TIER_RANK: Record<Tier, number> = {
    [WEBSITE]: 0,
    [STARTER]: 1,
    [GROWTH]: 2,
    [PRO]: 3,
    [AGENCY]: 4,
}

// Columns and modal width per number of plan cards, from Pro alone up to the Website plan and the three above it.
const GRID_COLUMNS: Record<number, string> = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
}
const MODAL_WIDTHS: Record<number, number> = {
    1: 520,
    2: 720,
    3: 960,
    4: 1160,
}

type PlanCard = {
    tier: Tier
    price: Pick<TierCardProps, 'price' | 'regularPrice' | 'priceSuffix' | 'priceNote'>
    features: PlanFeature[]
}

/**
 * The current plan and the plans above it side by side, the same cards as when subscribing, the next one recommended.
 * Agency below them, and on top one reason from the company's own usage, when there is one. Choosing a plan first
 * shows what the upgrade costs; only confirming that changes the subscription.
 */
export function UpgradeModal({ open, onClose, ...props }: UpgradeModalProps) {
    const { activeCompany, can, onTrial } = useAuth()
    const { t } = useTranslation()
    const upgradeMutation = useUpgradeTier()
    const planFeatures = usePlanFeatures()
    const tierPrice = useTierPrice()
    const { data: pricing } = usePricing()
    const showsUsage = open && can(SHOW_COMPANY_USAGE)
    const { data: usage } = useCompanyUsage(showsUsage ? (activeCompany?.id ?? '') : '')
    const [chosenTier, setChosenTier] = useState<Tier>()

    const currentTier = activeCompany?.tier
    const period = activeCompany?.period === 'yearly' ? 'yearly' : 'monthly'
    const currentRank = currentTier ? TIER_RANK[currentTier] : -1
    const higherTiers = ORDERED_TIERS.filter((tier) => TIER_RANK[tier] > currentRank)

    // Agency is never compared: it has no card of its own, only the line below the cards.
    const shownTiers = currentTier && currentTier !== AGENCY ? [currentTier, ...higherTiers] : []

    const planCard = (tier: Tier): PlanCard[] => {
        const tierPricing = pricing?.tiers[tier]
        const periodPricing = tierPricing?.[period]

        if (!pricing || !tierPricing || !periodPricing) {
            return []
        }

        const price = tierPrice(tierPricing, period, pricing.currency)

        if (!price) {
            return []
        }

        return [
            {
                tier,
                price,
                features: planFeatures(tierPricing, periodPricing.overageAmount, pricing.currency),
            },
        ]
    }

    const cards = shownTiers.flatMap(planCard)
    const featureRows = Math.max(0, ...cards.map(({ features }) => features.length))
    const chosenCard = cards.find((card) => card.tier === chosenTier)

    const handleClose = () => {
        setChosenTier(undefined)
        onClose()
    }

    const handleConfirm = (tier: Tier, prorationDate: number | undefined) => {
        upgradeMutation.mutate(
            {
                tier,
                prorationDate,
            },
            {
                onSuccess: () => {
                    handleClose()
                },
            }
        )
    }

    const handleContact = () => {
        window.open('mailto:hi@evaa.chat?subject=Agency Plan Inquiry', '_blank')
    }

    if (chosenCard && currentTier) {
        return (
            <Modal
                title={t('Upgrade to {{plan}}', { plan: t(`tier.${chosenCard.tier}`) })}
                open={open}
                onCancel={handleClose}
                footer={null}
                width={520}
                {...props}
            >
                <ConfirmUpgrade
                    currentTier={currentTier}
                    card={chosenCard}
                    period={period}
                    upgrading={upgradeMutation.isPending}
                    onBack={() => setChosenTier(undefined)}
                    onConfirm={handleConfirm}
                />
            </Modal>
        )
    }

    return (
        <Modal
            title={t('Upgrade subscription')}
            open={open}
            onCancel={handleClose}
            footer={null}
            width={MODAL_WIDTHS[cards.length] ?? 520}
            {...props}
        >
            {cards.length > 0 ? (
                <Flex
                    vertical
                    gap="middle"
                >
                    {usage?.upgradeReason && (
                        <UpgradeReasonLine
                            reason={usage.upgradeReason}
                            resetsAt={usage.resetsAt}
                        />
                    )}
                    {/* Room above the cards for the badge that overhangs their top edge. */}
                    <div className={clsx('mt-3 grid gap-4', GRID_COLUMNS[cards.length])}>
                        {cards.map((card) => (
                            <UpgradePlanCard
                                key={card.tier}
                                card={card}
                                current={card.tier === currentTier}
                                recommended={card.tier === higherTiers[0]}
                                featureRows={featureRows}
                                onChoose={setChosenTier}
                            />
                        ))}
                    </div>
                    <AgencyTier />
                    <Typography.Text
                        type="secondary"
                        className="text-xs"
                    >
                        {/* An upgrade keeps the trial: nothing is charged until it ends. */}
                        {onTrial
                            ? t('Your free trial continues. After it, you pay the price of your new plan.')
                            : t(
                                  'When you upgrade, you pay the difference for the rest of this billing period right away.'
                              )}
                    </Typography.Text>
                </Flex>
            ) : (
                <HighestPlan
                    onClose={handleClose}
                    onContact={handleContact}
                />
            )}
        </Modal>
    )
}

type UpgradePlanCardProps = {
    card: PlanCard
    current: boolean
    recommended: boolean
    featureRows: number
    onChoose: (tier: Tier) => void
}

function UpgradePlanCard({ card, current, recommended, featureRows, onChoose }: UpgradePlanCardProps) {
    const { t } = useTranslation()
    const { tier, price, features } = card

    return (
        <TierCard
            title={t(`tier.${tier}`)}
            description={t(TIER_TAGLINES[tier])}
            {...price}
            badge={recommended ? t('Recommended plan') : undefined}
            muted={current}
            selected={recommended}
            features={features}
            featureRows={featureRows}
            action={
                <Button
                    block
                    type={recommended ? 'primary' : undefined}
                    disabled={current}
                    onClick={() => onChoose(tier)}
                >
                    {current ? t('Current plan') : t('Upgrade to {{plan}}', { plan: t(`tier.${tier}`) })}
                </Button>
            }
        />
    )
}

type ConfirmUpgradeProps = {
    currentTier: Tier
    card: PlanCard
    period: 'monthly' | 'yearly'
    upgrading: boolean
    onBack: () => void
    onConfirm: (tier: Tier, prorationDate: number | undefined) => void
}

/**
 * The step between choosing a plan and the upgrade: the plan change, what it costs for the rest of the billing period
 * and when that is billed, and whether the company leaves prices of before.
 */
function ConfirmUpgrade({ currentTier, card, period, upgrading, onBack, onConfirm }: ConfirmUpgradeProps) {
    const { t } = useTranslation()
    const { data: pricing } = usePricing()
    const { data: preview, isLoading } = useUpgradePreview(card.tier)
    const plan = t(`tier.${card.tier}`)
    const amount = pricing?.tiers[card.tier]?.[period]?.amount ?? 0
    const currency = preview?.currency ?? pricing?.currency ?? 'EUR'
    const formattedAmount = formatPrice(amount, currency)

    const recurringPrice =
        period === 'yearly'
            ? t('{{price}} per year', { price: formattedAmount })
            : t('{{price}} per month', { price: formattedAmount })

    return (
        <Flex
            vertical
            gap="middle"
        >
            <Descriptions
                column={1}
                size="small"
                bordered
                items={[
                    {
                        key: 'current',
                        label: t('Current plan'),
                        children: t(`tier.${currentTier}`),
                    },
                    {
                        key: 'new',
                        label: t('New plan'),
                        children: `${plan} · ${recurringPrice}`,
                    },
                ]}
            />

            {isLoading ? (
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 2 }}
                />
            ) : (
                <Typography.Paragraph className="mb-0">
                    <UpgradeCost
                        preview={preview}
                        recurringPrice={recurringPrice}
                    />
                </Typography.Paragraph>
            )}

            {preview?.leavesOldPrices && (
                <Alert
                    type="warning"
                    showIcon
                    message={t(
                        'Your current plan keeps its prices and allowances from before. {{plan}} comes with the current prices and allowances shown on the plan.',
                        { plan }
                    )}
                />
            )}

            <Flex
                justify="flex-end"
                gap="small"
            >
                <Button
                    onClick={onBack}
                    disabled={upgrading}
                >
                    {t('Back')}
                </Button>
                <Button
                    type="primary"
                    loading={upgrading}
                    disabled={isLoading}
                    onClick={() => onConfirm(card.tier, preview?.prorationDate)}
                >
                    {t('Upgrade to {{plan}}', { plan })}
                </Button>
            </Flex>
        </Flex>
    )
}

type UpgradeCostProps = {
    preview: UpgradePreview | undefined
    recurringPrice: string
}

/**
 * During a trial: nothing until it ends. Otherwise what is charged right away for the rest of the billing period, or,
 * without a preview from Stripe, that the difference is.
 */
function UpgradeCost({ preview, recurringPrice }: UpgradeCostProps) {
    const { t } = useTranslation()

    if (preview?.trialEndsAt) {
        return t('Your free trial continues until {{date}}. After it, you pay {{price}}, excl. VAT.', {
            date: dayjs(preview.trialEndsAt).format('D MMMM YYYY'),
            price: recurringPrice,
        })
    }

    if (preview?.chargedNow == null || preview.chargedNowInclVat == null) {
        return t(
            'You pay the difference for the rest of this billing period right away, prorated. After that, you pay {{price}}, excl. VAT.',
            { price: recurringPrice }
        )
    }

    // Credit on the account, or fewer paid extra websites on the new plan, can cover the difference.
    if (preview.chargedNowInclVat <= 0) {
        return t('Nothing is charged now. After that, you pay {{price}}, excl. VAT.', { price: recurringPrice })
    }

    const amount = formatPrice(preview.chargedNow, preview.currency)

    // Without VAT, such as with the reverse charge, both amounts are the same.
    if (preview.chargedNowInclVat === preview.chargedNow) {
        return t(
            'For the rest of this billing period you pay {{amount}} now, excl. VAT. After that, you pay {{price}}, excl. VAT.',
            {
                amount,
                price: recurringPrice,
            }
        )
    }

    return t(
        'For the rest of this billing period you pay {{amount}} now, excl. VAT ({{amountInclVat}} incl. VAT). After that, you pay {{price}}, excl. VAT.',
        {
            amount,
            amountInclVat: formatPrice(preview.chargedNowInclVat, preview.currency),
            price: recurringPrice,
        }
    )
}

type HighestPlanProps = {
    onClose: () => void
    onContact: () => void
}

function HighestPlan({ onClose, onContact }: HighestPlanProps) {
    const { t } = useTranslation()

    return (
        <Flex
            vertical
            gap="middle"
        >
            <div>
                <Typography.Title level={4}>{t('Need more?')}</Typography.Title>
                <Typography.Paragraph>
                    {t("You're already on our highest self-service plan. Contact us about Agency.")}
                </Typography.Paragraph>
            </div>

            <Flex
                justify="flex-end"
                gap="small"
            >
                <Button onClick={onClose}>{t('Cancel')}</Button>
                <Button
                    type="primary"
                    onClick={onContact}
                >
                    {t('Contact us')}
                </Button>
            </Flex>
        </Flex>
    )
}

type UpgradeReasonLineProps = {
    reason: NonNullable<CompanyUsage['upgradeReason']>
    resetsAt: Date
}

/**
 * One sentence from the company's own usage: extra usage paid for, or a limit reached or almost reached, with what the
 * recommended plan includes.
 */
function UpgradeReasonLine({ reason, resetsAt }: UpgradeReasonLineProps) {
    const { t } = useTranslation()
    const number = (value: number | undefined) => (value ?? 0).toLocaleString('nl-NL')
    const values = reason.values
    const plan = t(`tier.${reason.tier}`)
    const included = number(values.included)
    const cost = formatPrice(values.cost ?? 0, 'EUR')
    const used = number(values.used)
    const allowance = number(values.allowance)
    const days = Math.max(1, Math.ceil(dayjs(resetsAt).diff(dayjs(), 'day', true)))

    const texts: Record<string, string> = {
        paid_extra_messages: t(
            'You paid {{cost}} this billing period for {{count}} extra chat messages. {{plan}} includes {{included}} per month.',
            {
                cost,
                count: values.count,
                plan,
                included,
            }
        ),
        paid_extra_candidates: t(
            'You paid {{cost}} this billing period for {{count}} extra candidates. {{plan}} includes {{included}} per month.',
            {
                cost,
                count: values.count,
                plan,
                included,
            }
        ),
        // The Website plan includes no candidates: every one is paid for.
        paid_candidates: t(
            'You paid {{cost}} this billing period for {{count}} candidates via WhatsApp. {{plan}} includes {{included}} per month.',
            {
                cost,
                count: values.count,
                plan,
                included,
            }
        ),
        paid_extra_websites: t('You pay for {{count}} extra websites. {{plan}} includes {{included}}.', {
            count: values.count,
            plan,
            included,
        }),
        messages_used_up: t(
            'Your chat messages are used up, so the chat is unavailable until your allowance renews. {{plan}} includes {{included}} per month.',
            {
                plan,
                included,
            }
        ),
        candidates_used_up: t(
            'You have used all candidates of this billing period. {{plan}} includes {{included}} per month.',
            {
                plan,
                included,
            }
        ),
        messages_nearly_used: t(
            'You have used {{used}} of your {{allowance}} chat messages, with {{count}} days to go. {{plan}} includes {{included}} per month.',
            {
                used,
                allowance,
                count: days,
                plan,
                included,
            }
        ),
        candidates_nearly_used: t(
            'You have used {{used}} of your {{allowance}} candidates, with {{count}} days to go. {{plan}} includes {{included}} per month.',
            {
                used,
                allowance,
                count: days,
                plan,
                included,
            }
        ),
    }

    if (!texts[reason.key]) {
        return null
    }

    return (
        <Alert
            type="info"
            showIcon
            icon={<RiseOutlined />}
            message={texts[reason.key]}
        />
    )
}
